// src/components/DesktopAppNote.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { deviceKind, runsDesktopApp } from '../utils/device';
import { useInstallerDownload, detectOS } from '../hooks/useInstallerDownload';
import SmartScreenHelp from './SmartScreenHelp';
import { apiPost } from '../api';
import { parseApiError } from '../apiError';
import { trackEvent } from '../analytics/plausible';
import { handoffEmailRecord, rememberHandoffEmail, HANDOFF_EMAIL_EVENT } from '../analytics/signup';
import { COPY } from './startPanelCopy';

// The empty bookshelf's start panel (#1124, from #938's note): the one step a new author
// needs next. On a computer, the installer for it, one click. Anywhere else, the link by
// email for their computer. Shown on /books and the signed-in home page, wherever the shelf
// is empty; it is the page they already land on, so nothing is forced on them. Both pages
// render it for subscribers only, so it has no no-plan state.

interface DesktopAppNoteProps {
  // accountId (the ID token's sub) keys the "we've emailed you" flag (#938).
  accountId: string | null;
  // 'hero' on a fresh account's empty shelf (#1130): the panel is the page, with a
  // screenshot of the app beside it. headingLevel is the panel title's level there.
  variant?: 'note' | 'hero';
  headingLevel?: 1 | 2;
}

type TitleAs = 'p' | 'h1' | 'h2';

const Title: React.FC<{ as: TitleAs; children: React.ReactNode }> = ({ as: Tag, children }) =>
  <Tag className="start-panel-title">{children}</Tag>;

const DAY_MS = 24 * 60 * 60 * 1000;

// `os` is read once, by DesktopAppNote, so one read decides both the panel and the screenshot.
const ComputerPanel: React.FC<{ titleAs: TitleAs; os: ReturnType<typeof detectOS> }> = ({ titleAs, os }) => {
  const other = os === 'mac' ? 'windows' : 'mac';
  const { downloading, downloadError, smartScreen, closeSmartScreen, started, start } = useInstallerDownload('bookshelf');
  const c = COPY.computer;

  // One always-mounted primary button: it is the SmartScreen help's return-focus target,
  // so it must still be there when the help closes (it becomes the retry once started).
  const primaryLabel = started ? c.retry : (os === 'mac' ? c.buttonMac : c.buttonWindows);

  return (
    <div className="start-panel">
      <Title as={titleAs}>{os === 'mac' ? c.headingMac : c.headingWindows}</Title>
      <p className="start-panel-status" role="status">
        {downloading ? c.busy : (started ? c.started : '')}
      </p>
      <button
        type="button"
        className={started ? 'start-panel-link' : 'btn-primary start-panel-download'}
        onClick={e => start('installer', started ?? os, e.currentTarget)}
        disabled={downloading}
      >
        {!started && <Download size={18} />}
        {primaryLabel}
      </button>
      {downloadError && <p className="dl-download-error" role="alert">{downloadError}</p>}
      <p className="start-panel-alt">
        <button type="button" className="start-panel-link" onClick={e => start('installer', other, e.currentTarget)} disabled={downloading}>
          {os === 'mac' ? c.otherFromMac : c.otherFromWindows}
        </button>
        {' · '}
        <Link to="/downloads" className="start-panel-link">{c.allOptions}</Link>
      </p>
      <ol className="start-panel-steps">
        {c.steps.map(s => <li key={s}>{s}</li>)}
      </ol>
      {smartScreen && (
        <SmartScreenHelp app={smartScreen.app} portable={smartScreen.portable} returnFocusTo={smartScreen.trigger} onClose={closeSmartScreen} />
      )}
    </div>
  );
};

type EmailState =
  | { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent'; address: string | null } | { kind: 'error'; message: string; code: string };

const ERROR_COPY: Record<string, string> = {
  HANDOFF_DAILY_LIMIT: COPY.away.dailyLimit,
  HANDOFF_ALREADY_SENT: COPY.away.alreadySent,
  HANDOFF_NOT_SENT: COPY.away.notSent,
  NO_EMAIL: COPY.away.notSent,
  SUBSCRIPTION_REQUIRED: COPY.noPlan,
  USES_DESKTOP: COPY.away.usesDesktop,
};

// Refusals that will come again on a retry: the email button goes, not just the message.
const FINAL_REFUSALS = new Set(['SUBSCRIPTION_REQUIRED', 'NO_EMAIL', 'HANDOFF_ALREADY_SENT', 'HANDOFF_DAILY_LIMIT', 'HANDOFF_NOT_SENT', 'USES_DESKTOP']);

const AwayPanel: React.FC<{ accountId: string | null; titleAs: TitleAs }> = ({ accountId, titleAs }) => {
  const a = COPY.away;
  const [record, setRecord] = useState(() => handoffEmailRecord(accountId));
  const [email, setEmail] = useState<EmailState>({ kind: 'idle' });
  const [linkState, setLinkState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const sharing = useRef(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  const shareButton = useRef<HTMLButtonElement>(null);
  // The downloads page, not this one: the link is for their computer.
  const downloadsUrl = `${window.location.origin}/downloads`;

  useEffect(() => {
    const read = () => setRecord(handoffEmailRecord(accountId));
    read();
    window.addEventListener(HANDOFF_EMAIL_EVENT, read);
    return () => window.removeEventListener(HANDOFF_EMAIL_EVENT, read);
  }, [accountId]);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  // The email button unmounts after a send or a final refusal; keep focus in the panel.
  const emailGone = email.kind === 'sent' || (email.kind === 'error' && FINAL_REFUSALS.has(email.code));
  useEffect(() => {
    if (emailGone) shareButton.current?.focus();
  }, [emailGone]);

  // Offer the email only when the server would send it: no send known, or a verified
  // address last sent to a day or more ago (an unverified one is sent the link once, ever).
  const canEmail = !emailGone && (
    !record || (record.verified && (record.at === null || Date.now() - Date.parse(record.at) >= DAY_MS))
  );

  const requestEmail = async () => {
    setEmail({ kind: 'sending' });
    try {
      const res = await apiPost('/profile/handoff-email');
      const data = (await res.body.json()) as unknown as { address?: unknown; verified?: unknown };
      const verified = data?.verified === true;
      setEmail({ kind: 'sent', address: typeof data?.address === 'string' && data.address ? data.address : null });
      trackEvent('Handoff Email Requested', { verified: verified ? 'yes' : 'no' });
      if (accountId) rememberHandoffEmail(accountId, verified);
    } catch (err: unknown) {
      const { code } = await parseApiError(err);
      setEmail({ kind: 'error', message: ERROR_COPY[code] ?? a.failed, code });
      // What the server told us about this address, so a reload stays honest.
      if (accountId && code === 'HANDOFF_ALREADY_SENT') rememberHandoffEmail(accountId, false);
      if (accountId && code === 'HANDOFF_DAILY_LIMIT') rememberHandoffEmail(accountId, true);
    }
  };

  // Share sheet first: it is better supported inside in-app browsers than the Clipboard
  // API. As on /downloads; a dismissed share sheet is not a failure. A second tap while
  // the sheet is open is ignored: a second share() rejects, which is not a failure either.
  const shareOrCopy = useCallback(async () => {
    if (sharing.current) return;
    sharing.current = true;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: a.shareTitle, url: downloadsUrl });
        trackEvent('Handoff Link Shared');
        return;
      }
      await navigator.clipboard.writeText(downloadsUrl);
      trackEvent('Handoff Link Shared');
      setLinkState('copied');
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setLinkState('idle'), 2500);
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return;
      setLinkState('failed');
    } finally {
      sharing.current = false;
    }
  }, [a.shareTitle, downloadsUrl]);

  const statusText = email.kind === 'sending' ? a.sending
    : email.kind === 'sent' ? (email.address ? a.sent(email.address) : a.emailedLine)
    : '';

  return (
    <div className="start-panel">
      <Title as={titleAs}>{a.heading}</Title>
      {email.kind !== 'sent' && <p className="start-panel-status">{record ? a.emailedLine : a.lede}</p>}
      <p className="start-panel-status" role="status">{statusText}</p>
      {canEmail && (
        <button type="button" className="btn-primary start-panel-download" onClick={requestEmail} disabled={email.kind === 'sending'}>
          {record ? a.emailAgain : a.emailButton}
        </button>
      )}
      {email.kind === 'error' && <p className="dl-download-error" role="alert">{email.message}</p>}
      {email.kind === 'error' && email.code === 'SUBSCRIPTION_REQUIRED' && (
        <Link to="/pricing" className="btn-primary">{COPY.seePlans}</Link>
      )}
      <button type="button" ref={shareButton} className="btn-secondary btn-sm" onClick={shareOrCopy}>
        {linkState === 'copied' ? a.copied : linkState === 'failed' ? a.copyFailed : a.share}
      </button>
      {linkState === 'failed' && <p className="dl-link-fallback">{a.copyFallback} <strong>{downloadsUrl}</strong></p>}
    </div>
  );
};

const DesktopAppNote: React.FC<DesktopAppNoteProps> = ({ accountId, variant = 'note', headingLevel = 1 }) => {
  const [canInstall] = useState(() => runsDesktopApp(deviceKind()));
  const [os] = useState(detectOS);
  const titleAs: TitleAs = variant === 'hero' ? (headingLevel === 2 ? 'h2' : 'h1') : 'p';
  const panel = canInstall ? <ComputerPanel titleAs={titleAs} os={os} /> : <AwayPanel accountId={accountId} titleAs={titleAs} />;
  if (variant !== 'hero') return panel;
  const mac = os === 'mac';
  const img = mac ? 'features-mac' : 'features-windows';
  return (
    <section className="start-hero">
      <div className="start-hero-main">
        <p className="start-hero-label">{COPY.hero.label}</p>
        {panel}
      </div>
      <img
        className="start-hero-shot"
        src={`/img/${img}.png`}
        srcSet={`/img/${img}.png 1440w, /img/${img}@2x.png 2880w`}
        sizes="(min-width: 900px) 560px, 100vw"
        alt={mac ? COPY.hero.altMac : COPY.hero.altWindows}
      />
    </section>
  );
};

export default DesktopAppNote;
